import React from 'react';
import XpheriumLogo from './XpheriumLogo';
import { AuthProductVisual } from './AuthBrandPanel';

const BENEFITS = [
  {
    title: 'Ask in plain English',
    body: 'Get charts, summaries, and answers without SQL.',
  },
  {
    title: 'Build live dashboards',
    body: 'Create scorecards and executive views in minutes.',
  },
  {
    title: 'Automate analysis',
    body: 'Forecast trends, monitor KPIs, and trigger workflows.',
  },
  {
    title: 'Deploy AI agents',
    body: 'Use your data and documents across chat, website, and team workflows.',
  },
];

const CALLOUTS = [
  {
    title: 'Understand performance faster',
    body: 'Combine business insights, forecasting, sentiment, and customer analytics.',
  },
  {
    title: 'Turn data into action',
    body: 'Build automations and alerts with Sentinel Intel.',
  },
  {
    title: 'Share what matters',
    body: 'Publish dashboards, reports, and AI assistants across your team.',
  },
];

const NUDGE_PLANS = [
  {
    id: 'free',
    name: 'Free',
    body: 'Explore analytics and upload your first data.',
  },
  {
    id: 'starter',
    name: 'Starter',
    body: 'For solo operators and analysts.',
  },
  {
    id: 'professional',
    name: 'Professional',
    body: 'For teams, automation, and AI agents.',
    featured: true,
  },
];

const FAQS = [
  {
    q: 'What is Intelligence Navigator?',
    a: 'A unified AI analytics workspace for insights, dashboards, forecasting, automation, and AI agents.',
  },
  {
    q: 'Do I need technical skills?',
    a: 'No. Connect or upload data and ask questions in plain English.',
  },
  {
    q: 'Is there a free plan?',
    a: 'Yes. Start free with no credit card. Upgrade when you need more capacity.',
  },
];

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function AuthTopNav({ mode, onSignIn, onCreateAccount }) {
  const linkClass = 'text-[13px] text-slate-500 hover:text-slate-900 transition-colors';
  return (
    <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-5 sm:px-8">
        <a href="https://www.xpherium.com" className="flex items-center gap-2.5" aria-label="Xpherium">
          <XpheriumLogo variant="dark" size="sm" className="h-7 w-auto" />
        </a>
        <nav className="flex items-center gap-4 sm:gap-6">
          <button type="button" className={`${linkClass} hidden sm:inline`} onClick={() => scrollToId('auth-story')}>Features</button>
          <button type="button" className={linkClass} onClick={() => scrollToId('auth-plans')}>Pricing</button>
          {mode === 'signin' ? (
            <button
              type="button"
              onClick={onCreateAccount}
              className="rounded-full bg-slate-900 px-3.5 py-1.5 text-[13px] font-medium text-white"
            >
              Create free account
            </button>
          ) : (
            <button type="button" onClick={onSignIn} className="text-[13px] font-medium text-slate-800">
              Sign in
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

function ScorecardMock() {
  const rows = [
    ['Revenue', '$1.24M', '+8.4%'],
    ['Active customers', '1,842', '+3.1%'],
    ['Repeat rate', '41%', '+1.6%'],
  ];
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">Executive scorecard</p>
      <div className="mt-4 space-y-3">
        {rows.map(([label, value, delta]) => (
          <div key={label} className="flex items-baseline justify-between border-b border-slate-100 pb-3 last:border-0 last:pb-0">
            <span className="text-[13px] text-slate-500">{label}</span>
            <span className="text-[15px] font-medium text-slate-900">{value}</span>
            <span className="text-[12px] text-sky-700">{delta}</span>
          </div>
        ))}
      </div>
      <div className="mt-5 flex h-16 items-end gap-1.5">
        {[36, 48, 42, 58, 52, 70, 64, 82].map((h) => (
          <div key={h} className="flex-1 rounded-sm bg-sky-500/80" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

function ChatMock() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">Business Insights</p>
      <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-[14px] text-slate-800">
        Where did margin move last quarter?
      </div>
      <p className="mt-4 text-[13px] leading-relaxed text-slate-500">
        Margin improved in two regions and slipped where discounting increased.
      </p>
      <div className="mt-4 flex h-20 items-end gap-2">
        {[40, 62, 48, 78, 55, 86].map((h) => (
          <div key={h} className="flex-1 rounded-t-md bg-gradient-to-t from-sky-600 to-sky-300" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

function WorkflowMock() {
  const steps = ['KPI moved', 'Summary drafted', 'Team notified'];
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">Sentinel Intel</p>
      <div className="mt-4 space-y-3">
        {steps.map((step, i) => (
          <div key={step} className="flex items-center gap-3">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[11px] text-white">{i + 1}</span>
            <span className="text-[14px] text-slate-700">{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AuthHero({ mode, onComparePlans }) {
  const signup = mode === 'signup';
  return (
    <div className="px-6 py-10 sm:px-10 sm:py-14 lg:px-12 lg:py-16 xl:px-16">
      <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-sky-700">
        Intelligence Navigator
      </p>
      <h1 className="mt-4 max-w-xl text-[2.15rem] font-medium leading-[1.08] tracking-[-0.03em] text-slate-900 sm:text-[2.75rem]">
        {signup ? 'Get started with Intelligence Navigator' : 'Turn business data into decisions'}
      </h1>
      <p className="mt-5 max-w-lg text-[16px] leading-relaxed text-slate-600">
        {signup
          ? 'The unified AI analytics workspace for business teams.'
          : 'A unified AI analytics workspace for insights, dashboards, forecasting, automation, and AI agents.'}
      </p>
      <div className="mt-7 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onComparePlans}
          className="rounded-full bg-slate-900 px-4 py-2.5 text-[13px] font-medium text-white"
        >
          Compare plans
        </button>
        <button
          type="button"
          onClick={() => scrollToId('auth-tour')}
          className="rounded-full border border-slate-300 bg-white px-4 py-2.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
        >
          See product tour
        </button>
      </div>
      {signup && (
        <p className="mt-4 text-[13px] text-slate-500">
          No credit card required · Start free · Upgrade anytime
        </p>
      )}
      <div className="mt-10 max-w-[520px]">
        <AuthProductVisual />
      </div>
    </div>
  );
}

function Spotlight({ flip, title, body, children }) {
  return (
    <div className="grid items-center gap-10 py-16 lg:grid-cols-2 lg:gap-20 lg:py-24">
      <div className={flip ? 'lg:order-2' : ''}>
        <h3 className="max-w-md text-[1.85rem] font-medium leading-tight tracking-[-0.02em] text-slate-900">
          {title}
        </h3>
        <p className="mt-4 max-w-md text-[15px] leading-relaxed text-slate-600">{body}</p>
      </div>
      <div className={flip ? 'lg:order-1' : ''}>{children}</div>
    </div>
  );
}

export function AuthBelowFold({ onChoosePlan }) {
  return (
    <div className="border-t border-slate-200/80 bg-white">
      <section id="auth-story" className="mx-auto max-w-6xl px-6 py-20 sm:px-8 lg:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <h2 className="text-[2rem] font-medium leading-tight tracking-[-0.03em] text-slate-900 sm:text-[2.35rem]">
              One platform for business intelligence and AI
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-slate-600">
              Connect your data, ask questions in plain English, build dashboards, automate reporting, and deploy AI agents — all from one workspace.
            </p>
          </div>
          <div className="space-y-6">
            {BENEFITS.map((item) => (
              <div key={item.title}>
                <p className="text-[15px] font-medium text-slate-900">{item.title}</p>
                <p className="mt-1 text-[14px] leading-relaxed text-slate-500">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="auth-tour" className="bg-[#f6f8fb]">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-20 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:py-28">
          <ScorecardMock />
          <div className="space-y-8">
            {CALLOUTS.map((item) => (
              <div key={item.title}>
                <p className="text-[16px] font-medium text-slate-900">{item.title}</p>
                <p className="mt-1.5 max-w-md text-[14px] leading-relaxed text-slate-500">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 sm:px-8">
        <Spotlight
          title="Ask anything about your business data"
          body="From revenue trends to KPI scorecards, Intelligence Navigator helps non-technical teams get answers quickly."
        >
          <ChatMock />
        </Spotlight>
        <Spotlight
          flip
          title="Build dashboards and scorecards without complexity"
          body="Create executive dashboards, compare metrics, and keep teams aligned with live reporting."
        >
          <ScorecardMock />
        </Spotlight>
        <Spotlight
          title="Automate follow-up actions"
          body="Trigger notifications, summaries, and workflows when something changes in your data."
        >
          <WorkflowMock />
        </Spotlight>
      </section>

      <section className="border-y border-slate-200/80 bg-[#f6f8fb]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 py-8 text-[13px] text-slate-500 sm:px-8">
          {['AI analytics', 'Forecasting', 'Dashboards', 'Automation', 'AI agents'].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </section>

      <section id="auth-plans" className="mx-auto max-w-5xl px-6 py-20 sm:px-8 lg:py-24">
        <h2 className="text-center text-[1.85rem] font-medium tracking-[-0.02em] text-slate-900">
          Start free. Upgrade when you need more power.
        </h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {NUDGE_PLANS.map((plan) => (
            <button
              key={plan.id}
              type="button"
              onClick={() => onChoosePlan(plan.id)}
              className={`rounded-2xl border p-5 text-left transition-colors ${
                plan.featured
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300'
              }`}
            >
              <p className="text-[15px] font-medium">{plan.name}</p>
              <p className={`mt-2 text-[13px] leading-relaxed ${plan.featured ? 'text-white/70' : 'text-slate-500'}`}>
                {plan.body}
              </p>
            </button>
          ))}
        </div>
        <p className="mt-6 text-center text-[13px] text-slate-400">No credit card required for the free plan</p>
      </section>

      <section className="border-t border-slate-200/80">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:px-8">
          <h2 className="text-[1.25rem] font-medium text-slate-900">Questions</h2>
          <div className="mt-6 divide-y divide-slate-200">
            {FAQS.map((item) => (
              <div key={item.q} className="py-4">
                <p className="text-[14px] font-medium text-slate-800">{item.q}</p>
                <p className="mt-1 text-[14px] leading-relaxed text-slate-500">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
